/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Detail Page - View specific feedback and admin response
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  MessageSquare,
  Calendar,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  Image as ImageIcon
} from 'lucide-react'
import feedbackService from '../services/feedbackService'
import FeedbackCategoryBadge from '../components/feedback/FeedbackCategoryBadge'
import FeedbackStatusBadge from '../components/feedback/FeedbackStatusBadge'
import FeedbackRatingDisplay from '../components/feedback/FeedbackRatingDisplay'
import LoadingSpinner from '../components/LoadingSpinner'
import { stripHTML } from '../utils/sanitization'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('FeedbackDetail')

const FeedbackDetail = () => {
  const { feedbackId } = useParams()
  const navigate = useNavigate()
  const [feedback, setFeedback] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadFeedback = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await feedbackService.getFeedbackById(feedbackId)
      setFeedback(data)
    } catch (error) {
      logger.error('Error loading feedback:', error)
      const errorMessage = error.response?.data?.error || 'Failed to load feedback'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [feedbackId])

  useEffect(() => {
    loadFeedback()
  }, [loadFeedback])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-900 dark:text-red-100 mb-2">
            Failed to Load Feedback
          </h2>
          <p className="text-red-700 dark:text-red-300 mb-4">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!feedback) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
            Feedback Not Found
          </h2>
          <p className="text-yellow-700 dark:text-yellow-300 mb-4">
            The feedback you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Your Feedback</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                ID: {feedback.id}
              </p>
            </div>
          </div>
          <FeedbackStatusBadge status={feedback.status} />
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Submitted {formatDate(feedback.createdAt)}</span>
          </div>
          <FeedbackCategoryBadge category={feedback.category} />
          {feedback.rating && <FeedbackRatingDisplay rating={feedback.rating} />}
        </div>
      </div>

      {/* Feedback Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your Message
        </h2>
        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {stripHTML(feedback.message)}
        </p>

        {/* Screenshot */}
        {(feedback.screenshotUrl || feedback.screenshot) && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              Screenshot
            </h3>
            <a
              href={feedback.screenshotUrl || feedback.screenshot}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src={feedback.screenshotUrl || feedback.screenshot}
                alt="Feedback screenshot"
                className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity cursor-pointer"
              />
            </a>
          </div>
        )}

        {/* Context */}
        {feedback.context && Object.keys(feedback.context).length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Context</h3>
            <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
              {JSON.stringify(feedback.context, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Admin Response */}
      {(feedback.status === 'resolved' || feedback.status === 'closed') &&
        feedback.adminResponse && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="text-lg font-semibold text-green-900 dark:text-green-100">
                Response from {feedback.responder?.firstName} {feedback.responder?.lastName}
              </h2>
            </div>
            <p className="text-green-800 dark:text-green-200 whitespace-pre-wrap mb-2">
              {stripHTML(feedback.adminResponse)}
            </p>
            {feedback.respondedAt && (
              <p className="text-sm text-green-600 dark:text-green-400">
                Responded {formatDate(feedback.respondedAt)}
              </p>
            )}
          </div>
        )}

      {/* Pending Status */}
      {feedback.status === 'pending' && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <h2 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
              Under Review
            </h2>
          </div>
          <p className="text-yellow-800 dark:text-yellow-200">
            Our team is reviewing your feedback and will respond soon. You'll receive an email
            notification when we respond.
          </p>
        </div>
      )}

      {/* Closed without response */}
      {feedback.status === 'closed' && !feedback.adminResponse && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Closed</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300">
            This feedback has been reviewed and closed by our team.
          </p>
        </div>
      )}
    </div>
  )
}

export default FeedbackDetail
